import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import {
  Plus, CheckCircle, Trash2, Edit3, X, Save,
  Filter, ChevronDown, Calendar, Search, User
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import './Todos.css';

// Safely parse dates that may be ISO strings, Firestore Timestamps, or Date objects
const safeParseDate = (val) => {
  if (!val) return null;
  if (val.toDate) return val.toDate(); // Firestore Timestamp
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    try { return parseISO(val); } catch { return null; }
  }
  return null;
};
const safeFmt = (val, fmt) => {
  const d = safeParseDate(val);
  return d ? format(d, fmt) : '';
};

const CATEGORIES = [
  { value: 'sales', label: 'Sales', icon: '💰' },
  { value: 'billing', label: 'Billing', icon: '💳' },
  { value: 'team', label: 'Team', icon: '👥' },
  { value: 'ops', label: 'Ops', icon: '🔧' },
  { value: 'marketing', label: 'Marketing', icon: '📣' },
];

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'var(--red)' },
  { value: 'medium', label: 'Medium', color: 'var(--gold)' },
  { value: 'low', label: 'Low', color: 'var(--text-muted)' },
];

function Todos() {
  const { state, addTodo, updateTodo, toggleTodo, deleteTodo } = useApp();
  const { todos, employees } = state;

  // Active employees + admin for assignment
  const assignableUsers = useMemo(() => {
    const active = employees.filter(e => e.status === 'active')
      .map(e => ({ id: e.id, name: `${e.firstName} ${e.lastName}`.trim() }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: 'admin', name: 'Sarah (Admin)' }, ...active];
  }, [employees]);

  const [filter, setFilter] = useState('active');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [newTodo, setNewTodo] = useState({
    text: '', priority: 'medium', category: 'ops', dueDate: '', assignedTo: '',
  });

  // Filter & sort
  const filteredTodos = useMemo(() => {
    return todos
      .filter(t => {
        if (filter === 'active' && t.done) return false;
        if (filter === 'done' && !t.done) return false;
        if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
        if (assigneeFilter !== 'all') {
          if (assigneeFilter === 'unassigned' && t.assignedTo) return false;
          if (assigneeFilter !== 'unassigned' && t.assignedTo !== assigneeFilter) return false;
        }
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          return t.text.toLowerCase().includes(s) || (t.assignedToName || '').toLowerCase().includes(s);
        }
        return true;
      })
      .sort((a, b) => {
        if (a.done !== b.done) return a.done ? 1 : -1;
        const p = { high: 0, medium: 1, low: 2 };
        if ((p[a.priority] ?? 1) !== (p[b.priority] ?? 1)) {
          return (p[a.priority] ?? 1) - (p[b.priority] ?? 1);
        }
        const da = safeParseDate(a.createdAt) || new Date(0);
        const db2 = safeParseDate(b.createdAt) || new Date(0);
        return db2 - da;
      });
  }, [todos, filter, categoryFilter, assigneeFilter, searchTerm]);

  // Stats
  const stats = useMemo(() => ({
    total: todos.length,
    active: todos.filter(t => !t.done).length,
    done: todos.filter(t => t.done).length,
    highPriority: todos.filter(t => !t.done && t.priority === 'high').length,
  }), [todos]);

  const handleAdd = async () => {
    if (!newTodo.text.trim()) return;
    const assignee = assignableUsers.find(u => u.id === newTodo.assignedTo);
    await addTodo({
      ...newTodo,
      dueDate: newTodo.dueDate || null,
      assignedTo: newTodo.assignedTo || null,
      assignedToName: assignee?.name || null,
    });
    setNewTodo({ text: '', priority: 'medium', category: 'ops', dueDate: '', assignedTo: '' });
    setShowAdd(false);
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditForm({
      text: todo.text || '',
      priority: todo.priority || 'medium',
      category: todo.category || 'ops',
      dueDate: todo.dueDate || '',
      assignedTo: todo.assignedTo || '',
    });
  };

  const saveEdit = async (id) => {
    if (!editForm.text.trim()) return;
    const assignee = assignableUsers.find(u => u.id === editForm.assignedTo);
    await updateTodo(id, {
      text: editForm.text.trim(),
      priority: editForm.priority,
      category: editForm.category,
      dueDate: editForm.dueDate || null,
      assignedTo: editForm.assignedTo || null,
      assignedToName: assignee?.name || null,
    });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this task?')) {
      await deleteTodo(id);
    }
  };

  const clearCompleted = async () => {
    if (!window.confirm(`Delete ${stats.done} completed task${stats.done > 1 ? 's' : ''}?`)) return;
    const doneTodos = todos.filter(t => t.done);
    for (const t of doneTodos) {
      await deleteTodo(t.id);
    }
  };

  const priorityStyle = (p) => ({
    high: { color: 'var(--red)', bg: 'rgba(239,68,68,0.08)' },
    medium: { color: 'var(--gold)', bg: 'rgba(184,146,106,0.1)' },
    low: { color: 'var(--text-muted)', bg: 'var(--cream)' },
  }[p] || { color: 'var(--text-muted)', bg: 'var(--cream)' });

  const catConfig = (cat) => CATEGORIES.find(c => c.value === cat) || { icon: '📋', label: cat };

  return (
    <div className="todos-page">
      {/* Header */}
      <div className="todos-header">
        <div>
          <h1>To-Do List</h1>
          <p className="todos-subtitle">
            {stats.active} active task{stats.active !== 1 ? 's' : ''}
            {stats.highPriority > 0 && <span className="todos-urgent"> · {stats.highPriority} high priority</span>}
          </p>
        </div>
        <button className="todos-add-btn" onClick={() => setShowAdd(true)}>
          <Plus size={16} /> New Task
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="todos-add-card">
          <div className="todos-add-row">
            <input
              autoFocus
              value={newTodo.text}
              onChange={e => setNewTodo(p => ({ ...p, text: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              placeholder="What needs to be done?"
              className="todos-add-input"
            />
          </div>
          <div className="todos-add-controls">
            <div className="todos-add-selects">
              <select value={newTodo.priority} onChange={e => setNewTodo(p => ({ ...p, priority: e.target.value }))}>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label} Priority</option>)}
              </select>
              <select value={newTodo.category} onChange={e => setNewTodo(p => ({ ...p, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
              </select>
              <select value={newTodo.assignedTo} onChange={e => setNewTodo(p => ({ ...p, assignedTo: e.target.value }))}>
                <option value="">Assign to...</option>
                {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input
                type="date"
                value={newTodo.dueDate}
                onChange={e => setNewTodo(p => ({ ...p, dueDate: e.target.value }))}
                className="todos-date-input"
              />
            </div>
            <div className="todos-add-actions">
              <button className="todos-save-btn" onClick={handleAdd}>Add Task</button>
              <button className="todos-cancel-btn" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="todos-toolbar">
        <div className="todos-search-wrap">
          <Search size={16} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search tasks..."
          />
        </div>
        <div className="todos-filter-pills">
          {['active', 'all', 'done'].map(f => (
            <button key={f} className={`todos-pill ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'active' ? `Active (${stats.active})` : f === 'done' ? `Done (${stats.done})` : `All (${stats.total})`}
            </button>
          ))}
        </div>
        <div className="todos-category-pills">
          <button className={`todos-pill sm ${categoryFilter === 'all' ? 'active' : ''}`} onClick={() => setCategoryFilter('all')}>All</button>
          {CATEGORIES.map(c => (
            <button key={c.value} className={`todos-pill sm ${categoryFilter === c.value ? 'active' : ''}`} onClick={() => setCategoryFilter(c.value)}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
        <div className="todos-assignee-filter">
          <User size={14} />
          <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
            <option value="all">All People</option>
            <option value="unassigned">Unassigned</option>
            {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="todos-list">
        {filteredTodos.length === 0 && (
          <div className="todos-empty">
            {filter === 'active' ? '🎉 All caught up! No active tasks.' : 'No tasks match your filters.'}
          </div>
        )}
        {filteredTodos.map(t => {
          const ps = priorityStyle(t.priority);
          const cc = catConfig(t.category);
          const isEditing = editingId === t.id;

          return (
            <div key={t.id} className={`todos-item ${t.done ? 'done' : ''} ${isEditing ? 'editing' : ''}`}>
              {!isEditing && (
                <div className={`todos-check ${t.done ? 'checked' : ''}`}
                  style={!t.done ? { borderColor: ps.color } : {}}
                  onClick={() => toggleTodo(t.id)}
                >
                  {t.done && <CheckCircle size={16} />}
                </div>
              )}

              <div className="todos-item-body">
                {isEditing ? (
                  <div className="todos-edit-form">
                    <input
                      autoFocus
                      value={editForm.text}
                      onChange={e => setEditForm(p => ({ ...p, text: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(t.id); if (e.key === 'Escape') setEditingId(null); }}
                      className="todos-edit-input"
                      placeholder="Task description..."
                    />
                    <div className="todos-edit-selects">
                      <select value={editForm.priority} onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}>
                        {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                      <select value={editForm.category} onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                      </select>
                      <select value={editForm.assignedTo} onChange={e => setEditForm(p => ({ ...p, assignedTo: e.target.value }))}>
                        <option value="">Unassigned</option>
                        {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                      </select>
                      <input
                        type="date"
                        value={editForm.dueDate}
                        onChange={e => setEditForm(p => ({ ...p, dueDate: e.target.value }))}
                        className="todos-date-input"
                      />
                    </div>
                    <div className="todos-edit-actions">
                      <button className="todos-save-btn" onClick={() => saveEdit(t.id)}><Save size={14} /> Save</button>
                      <button className="todos-cancel-btn" onClick={() => setEditingId(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="todos-item-text" onClick={() => toggleTodo(t.id)}>{t.text}</div>
                    <div className="todos-item-meta">
                      <span className="todos-cat-tag">{cc.icon} {cc.label}</span>
                      {t.assignedToName && (
                        <span className="todos-assignee-tag"><User size={11} /> {t.assignedToName}</span>
                      )}
                      {t.dueDate && (
                        <span className="todos-due-tag">
                          <Calendar size={11} /> {safeFmt(t.dueDate, 'MMM d')}
                        </span>
                      )}
                      {t.createdAt && (
                        <span className="todos-created">Added {safeFmt(t.createdAt, 'MMM d')}</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {!isEditing && (
                <>
                  <span className="todos-priority-tag" style={{ color: ps.color, background: ps.bg }}>{t.priority}</span>
                  <div className="todos-item-actions">
                    <button className="todos-icon-btn" onClick={() => startEdit(t)} title="Edit"><Edit3 size={14} /></button>
                    <button className="todos-icon-btn danger" onClick={() => handleDelete(t.id)} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {stats.done > 0 && (
        <div className="todos-footer">
          <button className="todos-clear-btn" onClick={clearCompleted}>
            <Trash2 size={14} /> Clear {stats.done} completed task{stats.done > 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}

export default Todos;
